<template>
  <div>
    <label class="text-xs font-medium text-gray-600" v-if="label">
      {{ label }}
      <span v-if="required" class="text-red-700">*</span>
    </label>

    <v-combobox
      v-if="combobox"
      :key="`form-opt-cb-${comboStamp}`"
      :model-value="modelValue"
      :placeholder="placeholder"
      :variant="variant"
      :density="density"
      :hide-details="hideDetails"
      :color="color"
      :rules="rules"
      :bg-color="bgColor"
      :prepend-icon="prependIcon"
      :prepend-inner-icon="prependInnerIcon"
      :disabled="disabled"
      :item-value="itemValue"
      :item-title="itemTitle"
      :items="items"
      :multiple="multiple"
      :return-object="returnObject"
      :chips="chips"
      :closable-chips="closableChips"
      :clearable="clearable"
      :hide-no-data="hideNoData"
      :auto-select-first="autoSelectFirst"
      :loading="loading"
      @update:model-value="onComboboxModelUpdate"
      @update:search="onComboSearchUpdate"
      @blur="handleComboBlur"
      @input="$emit('input', $event.target.value)"
      @change="$emit('change', $event)"
      @click:clear="$emit('click:clear')"
    />
    <v-autocomplete
      v-else
      v-model="internalValue"
      :placeholder="placeholder"
      :variant="variant"
      :density="density"
      :hide-details="hideDetails"
      :color="color"
      :rules="rules"
      :bg-color="bgColor"
      :prepend-icon="prependIcon"
      :prepend-inner-icon="prependInnerIcon"
      :disabled="disabled"
      :item-value="itemValue"
      :item-title="itemTitle"
      :items="items"
      :multiple="multiple"
      :return-object="returnObject"
      :chips="chips"
      :closable-chips="closableChips"
      :clearable="clearable"
      :auto-select-first="autoSelectFirst"
      :loading="loading"
      @input="$emit('input', $event.target.value)"
      @change="$emit('change', $event)"
      @click:clear="$emit('click:clear')"
    >
      <template #selection="{ index }" v-if="multiple && singleLineSelection">
        <span
          v-if="index === 0"
          class="truncate text-xs font-normal max-w-full inline-block overflow-hidden whitespace-nowrap"
        >
          {{ formatMultipleSelectionText(internalValue) }}
        </span>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
export default {
  name: "FormOption",
  props: {
    label: { type: String },
    placeholder: { type: String, default: "" },
    modelValue: { type: [String, Number, Array, Object], default: undefined },
    required: { type: Boolean, default: false },
    variant: { type: String, default: "outlined" },
    density: { type: String, default: "compact" },
    hideDetails: { type: String, default: "auto" },
    color: { type: String, default: "#475569" },
    bgColor: { type: String },
    disabled: { type: Boolean, default: false },
    itemValue: { type: String, default: "value" },
    itemTitle: { type: String, default: "title" },
    multiple: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    rules: { type: Array, default: () => [] },
    prependIcon: { type: String, default: null },
    prependInnerIcon: { type: String, default: null },
    returnObject: { type: Boolean, default: false },
    chips: { type: Boolean, default: false },
    closableChips: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    /** Ketik lalu cocokkan ke `items` (nilai awal bisa kosong / `null`). */
    combobox: { type: Boolean, default: false },
    hideNoData: { type: Boolean, default: false },
    /** Sorot opsi pertama yang cocok saat filter (memudahkan Enter / pilihan). */
    autoSelectFirst: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    singleLineSelection: { type: Boolean, default: true },
  },
  data() {
    return {
      comboSearchDraft: "",
      /** Naik saat nilai ditolak agar combobox di-remount (Vuetify tidak selalu reset tampilan). */
      comboStamp: 0,
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
        this.$emit("input", val);
        this.$emit("change", val);
      },
    },
  },
  methods: {
    /** Nilai mentah combobox → nilai `itemValue` (bila objek item). */
    normalizeComboboxVal(val) {
      if (val == null) return val;
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        this.itemValue in val
      ) {
        return val[this.itemValue];
      }
      return val;
    },
    /** Apakah nilai sama dengan salah satu opsi di `items` (hanya ini yang boleh ke parent). */
    comboboxValueAllowed(val) {
      const v = this.normalizeComboboxVal(val);
      if (v === null || v === undefined) return true;
      const iv = this.itemValue;
      const rows = this.items || [];
      for (const row of rows) {
        if (row == null || typeof row !== "object") continue;
        const opt = row[iv];
        if (opt === v) return true;
        if (String(opt) === String(v)) return true;
        const nv = Number(v);
        const no = Number(opt);
        if (
          Number.isFinite(nv) &&
          Number.isFinite(no) &&
          nv === no &&
          String(v).trim() !== "" &&
          String(v).trim() === String(nv)
        ) {
          return true;
        }
      }
      return false;
    },
    /** Nilai kanonik dari `items` untuk nilai yang sudah diizinkan. */
    canonicalItemValue(val) {
      const v = this.normalizeComboboxVal(val);
      if (v === null || v === undefined) return v;
      const iv = this.itemValue;
      for (const row of this.items || []) {
        if (row == null || typeof row !== "object") continue;
        const opt = row[iv];
        if (opt === v) return opt;
        if (String(opt) === String(v)) return opt;
        const nv = Number(v);
        const no = Number(opt);
        if (
          Number.isFinite(nv) &&
          Number.isFinite(no) &&
          nv === no &&
          String(v).trim() !== "" &&
          String(v).trim() === String(nv)
        ) {
          return opt;
        }
      }
      return v;
    },
    bumpComboboxToParentModel() {
      const cur = this.modelValue;
      this.comboStamp += 1;
      this.$nextTick(() => {
        this.$emit("update:modelValue", cur);
        this.$emit("input", cur);
        this.$emit("change", cur);
      });
    },
    onComboboxModelUpdate(val) {
      if (!this.comboboxValueAllowed(val)) {
        this.bumpComboboxToParentModel();
        return;
      }
      const canon = this.canonicalItemValue(val);
      this.$emit("update:modelValue", canon);
      this.$emit("input", canon);
      this.$emit("change", canon);
    },
    onComboSearchUpdate(s) {
      this.comboSearchDraft = s ?? "";
      this.$emit("update:search", this.comboSearchDraft);
    },
    /** Cocokkan teks ke salah satu `items` (judul atau nilai angka). */
    matchSearchToItemValue(q) {
      const trimmed = String(q ?? "").trim();
      if (!trimmed) return undefined;
      const iv = this.itemValue;
      const it = this.itemTitle;
      const rows = this.items || [];
      for (const row of rows) {
        if (row == null || typeof row !== "object") continue;
        const title = row[it];
        if (String(title).trim().toLowerCase() === trimmed.toLowerCase()) {
          return row[iv];
        }
      }
      const n = Number(trimmed);
      if (Number.isFinite(n)) {
        for (const row of rows) {
          if (row == null || typeof row !== "object") continue;
          const val = row[iv];
          if (Number(val) === n) return val;
        }
      }
      return undefined;
    },
    /** Saat fokus keluar: jika teks sama dengan salah satu opsi, set model ke nilai opsi itu. */
    handleComboBlur(e) {
      this.$emit("blur", e);
      if (!this.combobox) return;
      const q = String(this.comboSearchDraft ?? "").trim();
      this.comboSearchDraft = "";
      if (q === "") {
        if (!this.comboboxValueAllowed(this.modelValue)) {
          this.bumpComboboxToParentModel();
        }
        return;
      }
      const matched = this.matchSearchToItemValue(q);
      if (matched !== undefined) {
        const canon = this.canonicalItemValue(matched);
        this.$emit("update:modelValue", canon);
        this.$emit("input", canon);
        this.$emit("change", canon);
      } else {
        this.bumpComboboxToParentModel();
      }
    },
    formatMultipleSelectionText(val) {
      if (!Array.isArray(val) || !val.length) return "";
      const selectedTitles = (this.items || [])
        .filter((item) => {
          if (item == null) return false;
          const itemVal = typeof item === "object" ? item[this.itemValue] : item;
          return val.includes(itemVal);
        })
        .map((item) => (typeof item === "object" ? item[this.itemTitle] : item));
      return selectedTitles.join(", ");
    },
  },
  emits: [
    "update:modelValue",
    "input",
    "change",
    "click:clear",
    "blur",
    "update:search",
  ],
};
</script>
